<template>
  <div id="analysis">
    <b-card no-body>
      <h6 style="color:blue">Here you can find information about the analysis results in the tables and plots displayed on this website.</h6>
      <b-tabs vertical v-model="tabIndex" card pills>
        <b-tab title="Overview">
          <h2 style="margin-bottom: 30px">Overview</h2>
          <ul>
            <li>
              <p align="left">This website displays only CHIME/FRB detections and results. Many of the terms within the glossary are defined in more detail in the <a href="">CHIME/FRB Catalog 1 Paper</a>.</p>
            </li>
            <li>
              <p align="left">If you would like help reading and plotting data from Catalog 1, check out <a href="https://chime-frb-open-data.github.io/"> this link </a> for tutorials. </p>
            </li>
            <li>
              <p align="left">The information provided on this website is on a best effort basis.  Data will be updated as soon as it is available. Missing information is usually due to calibration challenges.</p>
            </li>
            <li>
              <p align="left">The site currently shows information about only published repeating FRBs as well as newly discovered (currently unpublished) Galactic sources. Eventually, new sources will be added automatically and in real-time.</p>
            </li>
            <li>
              <p align="left">The events that are displayed here will also be visible on the <b><i>TNS</i></b>. Eventually, we will also release real-time events to <b><i>VOEvent</i></b>.</p>
            </li>
            <li>
              <p align="left"><b><i>Last Updated</i></b> : Displays the time when any new information was added to this website (not necessarily a new source or burst).</p>
            </li>
            <li>
              <p align="left">Reading downloaded catalog file in python:
                <b-card no-body bg-variant="light" text-variant="dark">
                  >>> import json
                  <br>
                  >>> with open('repeaters.json', 'rb') as f: <br>
                  >>> &nbsp;&nbsp;&nbsp;&nbsp;catalog = json.loads(f.read())<br>
                  >>> print (list(catalog.keys()))<br><br>
                  <font size=2><i>['180814.J0422+73', '180908.J1232+74', '180916.J0158+65', '181017.J1705+68', '181030.J1054+73', '181119.J12+65', '181128.J0456+63', '190116.J1249+27', '190117.J2207+17', '190208.J1855+46', '190209.J0937+77', '190212.J18+81', '190213.J02+20', '190222.J2052+69', '190303.J1353+48', '190417.J1939+59', '190604.J1435+53', '190907.J08+46']</i></font><br>
                  >>> print (list(catalog['180908.J1232+74'].keys()))<br><br>
                  <font size=2><i>['180908', '190621', '190702', '190718', 'last_burst_date', 'ymw16', 'gl', 'dec', 'dm', 'gb', 'publication', 'ne2001', 'localized', 'ra']</i></font>
                </b-card>
              </p>
            </li>
          </ul>
        </b-tab>
        <b-tab title="Catalog 1 Glossary" active>
          <h2 style="margin-bottom: 30px">Catalog 1 Glossary </h2>
          <b-table
              style="text-align:left"
              hover
              striped
              outlined
              show-empty
              :stacked="md"
              :fields="catalog_fields"
              :items="catalog_items"
              small=true>
          </b-table>
        </b-tab>
        <b-tab title="Repeating FRBs">
          <h2 style="margin-bottom: 30px">{{ $t('navRepeaters') }} Page</h2>
          <ul align="left">
            <li>
              <p align="left">The table by default is sorted in descending order of <b><i>Latest Event</i></b>.</p>
            </li>
            <li>
              <p align="left">Repeaters that have a burst in the past 10 days are highlighted in red.</p>
            </li>
            <li>
              <p align="left">The <b><i>DM</i></b> shown in the table is the average DM of all the bursts (some of which are maximizing SNR and others structure depending on the burst morphology).</p>
            </li>
            <li>
              <p align="left"><b><i>RA</i></b> and <b><i>Dec</i></b> displayed are currently the best positions after combining the positions from all the bursts. Once we have better localization products using intensity and baseband data, information will be updated in the table.</p>
            </li>
          </ul>
        </b-tab>
        <b-tab title="Individual FRB source">
          <h2 style="margin-bottom: 30px">Individual FRB Page</h2>
          <b-tabs v-model="tabIndex" card>
            <b-tab title="Localization">
              <p>CHIME/FRB Localization</p>
              <font size=2>Our current localizations are based on per-beam S/N values for all available bursts from a given source. The R.A. of the beam centers for each detection are shown as black ticks on the bottom of the plot. Solid and dashed contours correspond to 68% and 95% confidence intervals respectively, with the colorscale mapping to the full confidence range. The table lists the extent for the 68% and 95% intervals for the centroid region.</font>
              <b-img-lazy
                  src="https://storage.googleapis.com/chimefrb-dev.appspot.com/FRB20181119A/localization.png"
                  width='600px'
                  alt="coming soon..."
                  fluid
                  center
              />
              <ul align="left">
                <li>
                  <p align="left">Reading downloaded localization file in python:
                    <b-card no-body bg-variant="light" text-variant="dark">
                      >>> import pickle
                      <br>
                      >>> with open('localization_contour.pkl', 'rb') as f: <br>
                      >>> &nbsp;&nbsp;&nbsp;&nbsp;localization_info = pickle.load(f)<br>
                      >>> print (list(localization_info.keys()))<br><br>
                      <font size=2><i>['event_id', 'Z', 'extent', 'results']</i></font><br>
                      >>> print (localization_info['Z'])<br><br>
                      <font size=2><i>array([[1.        , 1.        , 1.        , ..., 1.        , 1.        ,
                        1.        ],
                        [1.        , 1.        , 0.99998   , ..., 1.        , 1.        ,
                        1.        ],
                        [0.99998   , 0.999915  , 0.99814999, ..., 1.        , 1.        ,
                        1.        ],
                        ...,
                        [1.        , 1.        , 1.        , ..., 1.        , 1.        ,
                        1.        ],
                        [1.        , 1.        , 1.        , ..., 1.        , 1.        ,
                        1.        ],
                        [1.        , 1.        , 1.        , ..., 1.        , 1.        ,
                        1.        ]])</i></font>
                    </b-card>
                  </p>
                </li>
              </ul>
            </b-tab>
            <b-tab title="Table Parameters">
              <ul align="left">
                <li>
                  <p align="left">By default, we publish the meta data from our real-time pipeline to the table which includes <b><i>Burst ID, Date, Topocentric UTC @ 400 MHz, DM and SNR</i></b>.
                  <ul>
                    <li> The real-time pipeline computes the arrival time, DM and SNR using box-car filters. Furthermore, DM and arrival time information are coarse-grained and that is why the errors on these numbers in the table are relatively large compared to the bursts that have results from our offline pipelines. The degree of coarse graining depends on the search tree of our de-dispersion algorithm, <b><i>Bonsai</i></b>.
                    </li>
                  </ul>
                  </p>
                </li>
                <li>
                  <p align="left">
                    Once our offline pipelines are run on the intensity and baseband data for the bursts and verified by members of the collaboration, additional information will be made available.
                  <ul>
                    <li>
                      Values for <b><i> Flux, Fluence, Pulse Width (@600 MHz), Scattering Times (@600 MHz)</i></b> will be displayed along with a <b><i>waterfall plot</i></b>.
                    </li>
                    <li>Values for <b><i>DM, SNR and Topocentric UTC @ 400 MHz</i></b> will also be updated.</li>
                    <li>This process of uploading the information is currently manual and will be automated once our offline pipelines are running as expected.</li>
                  </ul>
                  </p>
                </li>
              </ul>
            </b-tab>
          </b-tabs>
        </b-tab>
        <b-tab title="Galactic Candidates" disabled>
          <h2 style="margin-bottom: 30px">{{ $t('navGalactic') }} Page</h2>
        </b-tab>
        <b-tab title="FRBs"><h4 style="color:red;">This page is currently under construction. Please stay tuned.</h4></b-tab>
        <b-tab title="Contact Us">
          <h4>Contact information: </h4>
          <p>For General questions email us at <span style="color: green">chimefrb@gmail.com</span></p>
          <p>For Technical questions: <span style="color: green"><a style="color:green" href="https://github.com/chime-frb-open-data/community/issues">https://github.com/chime-frb-open-data/community/issues</a></span></p>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>

</template>

<script>
export default {
  name: 'analysis',
  data() {
    return {
      collapsed: false,
      catalog_fields: [
        {key: 'column_name', label: 'Parameter Name', sortable: true},
        {key: 'unit', label: 'Unit', sortable: false},
        {key: 'description', label: 'Description', sortable: false},
      ],
      catalog_items: []
    }
  },
  mounted() {
    this.catalogTable()
  },
  methods: {
    catalogTable(){
      this.catalog_items = [
        {'unit': '...', 'column_name': 'tns_name', 'description': 'TNS name of the burst'},
        {'unit': '...', 'column_name': 'previous_name', 'description': 'Previous name (if applicable)'},
        {'unit':'...', 'column_name':'repeater name', 'description':'Associated repeater name (if applicable)'},
        {'unit':'degrees', 'column_name':'ra', 'description':'Right ascension (J2000)'},
        {'unit':'degrees', 'column_name':'ra_err', 'description':'Right ascension error (68% confidence)'},
        {'unit':'degrees', 'column_name':'dec', 'description':'Declination (J2000)'},
        {'unit':'degrees', 'column_name':'dec_err', 'description':'Declination error (68% confidence)'},
        {'unit':'degrees', 'column_name':'gl', 'description':'Galactic longitude'},
        {'unit':'degrees', 'column_name':'gb', 'description':'Galactic latitude'},
        {'unit':'hour', 'column_name':'exp_up', 'description':'Exposure for upper transit of the source'},
        {'unit':'hour', 'column_name':'exp_up_err', 'description':'Exposure error for upper transit of the source'},
        {'unit':'hour', 'column_name':'exp_low', 'description':'Exposure for lower transit of the source'},
        {'unit':'hour', 'column_name':'exp_low_err', 'description':'Exposure error for lower transit of the source'},
        {'unit':'...', 'column_name':'bonsai_snr', 'description':'Detection SNR'},
        {'unit':'pc cm−3', 'column_name':'bonsai_dm', 'description':'Detection DM'},
        {'unit':'Jy ms', 'column_name':'low_ft_68', 'description':'Lower limit fluence threshold (68% confidence)'},
        {'unit':'Jy ms', 'column_name':'up_ft_68', 'description':'Upper limit fluence threshold (68% confidence)'},
        {'unit':'Jy ms', 'column_name':'low_ft_95', 'description':'Lower limit fluence threshold (95% confidence)'},
        {'unit':'Jy ms', 'column_name':'up_ft_95', 'description':'Upper limit fluence threshold (95% confidence)'},
        {'unit':'...', 'column_name':'snr_fitb', 'description':'SNR determined using the fitting algorithm fitburst'},
        {'unit':'pc cm−3', 'column_name':'dm_fitb', 'description':'DM determined using the fitting algorithm fitburst'},
        {'unit':'pc cm−3', 'column_name':'dm_fitb_err', 'description':'DM error determined using the fitting algorithm fitburst'},
        {'unit':'pc cm−3', 'column_name':'dm_exc_ne2001', 'description':'DM excess between DM determined by fitburst and NE2001 assuming the best-fit sky position of the source'},
        {'unit':'pc cm−3', 'column_name':'dm_exc_ymw16', 'description':'DM excess between DM determined by fitburst and YMW16 assuming the best-fit sky position of the source'},
        {'unit':'s', 'column_name':'bc_width', 'description':'Box car width of the pulse'},
        {'unit':'s', 'column_name':'scat_time', 'description':'Scattering time (at 600MHz)'},
        {'unit':'s', 'column_name':'scat_time_err', 'description':'Scattering time (at 600MHz) error'},
        {'unit':'Jy', 'column_name':'flux', 'description':'Flux'},
        {'unit':'Jy', 'column_name':'flux_err', 'description':'Flux error'},
        {'unit':'Jy ms', 'column_name':'fluence', 'description':'Fluence'},
        {'unit':'Jy ms', 'column_name':'fluence_err', 'description':'Fluence error'},
        {'unit':'...', 'column_name':'sub_num', 'description':'Sub-burst number (1 if the FRB has only one burst)'},
        {'unit':'MJD', 'column_name':'mjd_400', 'description':'Time of arrival in UTC at CHIME location (topocentric) with reference to 400.19 MHz for the specific sub-burst'},
        {'unit':'MJD', 'column_name':'mjd_400_err', 'description':'Time of arrival in UTC at CHIME location (topocentric) error with reference to 400.19 MHz for the specific sub-burst'},
        {'unit':'MJD', 'column_name':'mjd_inf', 'description':'Time of arrival in UTC at CHIME location (topocentric) with reference to infinite frequency for the specific sub-burst'},
        {'unit':'MJD', 'column_name':'mjd_inf_err', 'description':'Time of arrival in UTC at CHIME location (topocentric) error with reference to infinite frequency for the specific sub-burst'},
        {'unit':'s', 'column_name':'width_fitb', 'description':'Width of sub-burst using fitburst'},
        {'unit':'s', 'column_name':'width_fitb_err', 'description':'Width error of sub-burst using fitburst'},
        {'unit':'...', 'column_name':'sp_idx', 'description':'Spectral index for the sub-burst'},
        {'unit':'...', 'column_name':'sp_idx_err', 'description':'Spectral index error for the sub-burst'},
        {'unit':'...', 'column_name':'sp_run', 'description':'Spectral running for the sub-burst'},
        {'unit':'...', 'column_name':'sp_run_err', 'description':'Spectral running error for the sub-burst'},
        {'unit':'MHz', 'column_name':'high_freq', 'description':'Highest frequency band of detection for the sub-burst'},
        {'unit':'MHz', 'column_name':'low_freq', 'description':'Lowest frequency band of detection for the sub-burst'},
        {'unit':'MHz', 'column_name':'peak_freq', 'description':'Peak frequency for the sub-burst'},
        {'unit':'...', 'column_name':'chi_sq', 'description':'Chi-squared from fitburst'},
        {'unit':'...', 'column_name':'dof', 'description':'Number of degrees of freedom in fitburst'},
        {'unit':'...', 'column_name':'flag_frac', 'description':'Fraction of spectral channels flagged in fitburst'},

      ]
    }
  }
}
</script>

<style>
#team h1{
  margin-top: 50px;
  margin-bottom: 30px;
}
</style>

